import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Play,
} from "lucide-react";
import common from "../../../../utils/common";
import { cn } from "../../../../utils/utils";
import ReliableChatImage from "../ReliableChatImage";

const ImageAlbumModal = ({ isOpen, onClose, initialMedia, mediaList = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const thumbnailScrollRef = useRef(null);

  const handleNext = useCallback(() => {
    if (currentIndex < mediaList.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setScale(1);
      setRotation(0);
    }
  }, [currentIndex, mediaList.length]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setScale(1);
      setRotation(0);
    }
  }, [currentIndex]);

  useEffect(() => {
    if (isOpen && initialMedia) {
      const index = mediaList.findIndex(
        (m) =>
          m._id === initialMedia._id ||
          (m.img && m.img === initialMedia.img) ||
          (m.file && m.file === initialMedia.file),
      );
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
    // Reset view state when opening
    setScale(1);
    setRotation(0);
  }, [isOpen, initialMedia, mediaList]);

  // Scroll active thumbnail into view
  useEffect(() => {
    if (isOpen && thumbnailScrollRef.current) {
      const activeThumb = thumbnailScrollRef.current.querySelector(
        `[data-index="${currentIndex}"]`,
      );
      if (activeThumb) {
        activeThumb.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [currentIndex, isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleNext, handlePrev, onClose]);

  if (!isOpen) return null;

  const currentMedia = mediaList[currentIndex];
  if (!currentMedia) return null;

  const currentSrc =
    currentMedia.type === "video" ? currentMedia.file : currentMedia.img;

  const renderMainContent = () => {
    if (currentMedia.type === "video") {
      return (
        <video
          src={currentSrc}
          controls
          autoPlay
          className="max-w-full max-h-[85vh] object-contain"
          style={{ transform: `rotate(${rotation}deg)` }}
        />
      );
    }

    return (
      <ReliableChatImage
        src={currentSrc}
        previewSrc={currentMedia.previewSrc}
        alt="Preview"
        className="max-w-full max-h-[85vh] object-contain transition-transform duration-200"
        containerClassName="max-w-full max-h-[85vh]"
        style={{
          transform: `scale(${scale}) rotate(${rotation}deg)`,
        }}
        draggable={false}
      />
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex bg-black/95">
      {/* Main Content Area */}
      <div className="flex-1 relative flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-20 flex justify-between items-center p-4 bg-gradient-to-b from-black/80 to-transparent text-white">
          <div className="flex items-center gap-3">
            {/* <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-700 border border-gray-600">
              {currentMedia.avatarFrom ? (
                <img
                  src={currentMedia.avatarFrom}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs">
                  IMG
                </div>
              )}
            </div> */}
            <div>
              <p className="font-medium text-sm">
                {currentMedia.senderName || "Người gửi"}
              </p>
              <p className="text-xs text-gray-300">
                {common.getPreviewTimeMessage(currentMedia.dateAction)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* <button
              onClick={handleDownload}
              className="p-2 hover:bg-white/10 rounded-full text-white/80 hover:text-white transition-colors"
              title="Tải xuống"
            >
              <Download size={20} />
            </button> */}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-full text-white/80 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content View */}
        <div className="flex-1 flex items-center justify-center relative p-4">
          {/* Nav Buttons */}
          {currentIndex > 0 && (
            <button
              onClick={handlePrev}
              className="absolute left-4 p-3 bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors z-10 backdrop-blur-sm"
            >
              <ChevronLeft size={24} />
            </button>
          )}

          {renderMainContent()}

          {currentIndex < mediaList.length - 1 && (
            <button
              onClick={handleNext}
              className="absolute right-4 p-3 bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors z-10 backdrop-blur-sm"
            >
              <ChevronRight size={24} />
            </button>
          )}
        </div>

        {/* Bottom Utils Actions for Image */}
        {currentMedia.type !== "video" && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-black/60 backdrop-blur-md px-6 py-2 rounded-full text-white z-20">
            <button
              onClick={() => setScale((s) => Math.max(0.5, s - 0.2))}
              className="hover:text-blue-400"
            >
              <ZoomOut size={18} />
            </button>
            <span className="text-xs min-w-[3rem] text-center">
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={() => setScale((s) => Math.min(3, s + 0.2))}
              className="hover:text-blue-400"
            >
              <ZoomIn size={18} />
            </button>
            <div className="w-[1px] h-4 bg-white/20 mx-1"></div>
            <button
              onClick={() => setRotation((r) => r + 90)}
              className="hover:text-blue-400"
            >
              <RotateCw size={18} />
            </button>
          </div>
        )}
      </div>

      {/* Right Sidebar - Thumbnails */}
      <div className="w-[300px] border-l border-white/10 bg-[#1e1e1e] flex flex-col h-full">
        <div className="p-4 border-b border-white/10">
          <h3 className="text-white font-medium">
            Danh sách media ({mediaList.length})
          </h3>
        </div>

        <div
          className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar"
          ref={thumbnailScrollRef}
        >
          {mediaList.map((media, index) => {
            const isSelected = index === currentIndex;
            const src =
              media.type === "video" ? media.content || media.file : media.img; // Using video thumb (usually in 'content' if available, otherwise fallback) or img

            return (
              <div
                key={media._id || index}
                data-index={index}
                onClick={() => {
                  setCurrentIndex(index);
                  setScale(1);
                  setRotation(0);
                }}
                className={cn(
                  "cursor-pointer rounded-lg overflow-hidden border-2 relative aspect-video bg-black/50 group",
                  isSelected
                    ? "border-blue-500 ring-2 ring-blue-500/20"
                    : "border-transparent hover:border-white/30",
                )}
              >
                {media.type === "video" ? (
                  <>
                    <img
                      src={media.content} // Video thumbnail commonly stored in 'content'
                      alt="Video thumb"
                      className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }} // Fallback if no thumb
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center">
                        <Play
                          size={14}
                          className="text-white fill-white ml-0.5"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <ReliableChatImage
                    src={src}
                    previewSrc={media.previewSrc}
                    alt=""
                    className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity"
                    containerClassName="w-full h-full"
                  />
                )}

                {isSelected && (
                  <div className="absolute inset-0 bg-blue-500/10 pointer-events-none"></div>
                )}

                {/* Date overlay */}
                <div className="absolute bottom-1 right-2 text-[10px] text-white/90 drop-shadow-md bg-black/40 px-1 rounded">
                  {new Date(media.dateAction).toLocaleDateString("vi-VN", {
                    day: "2-digit",
                    month: "2-digit",
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ImageAlbumModal;
